import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface PavementChartProps {
    data: Array<{ [key: string]: any }>;
    xAxisLabel?: string;
    yAxisLabel?: string;
    method?: 'paved' | 'unpaved' | 'giroud' | 'leng';
    reinforcedData?: boolean;
    bcrData?: boolean;
}

const PavementLineChartComponent: React.FC<PavementChartProps> = ({ data, xAxisLabel = 'Load (kN)', yAxisLabel = 'Deflection (mm)', method = 'paved', reinforcedData = false, bcrData = false }) => {
    const chartData = data || [];

    // Helper function to get legend labels based on method
    const getLegendLabels = () => {
        switch (method) {
            case 'unpaved':
            case 'giroud':
            case 'leng':
                return {
                    unreinforced: 'UNR',
                    reinforced: 'GTX',
                    bcr: 'GG',
                };
            case 'paved':
            default:
                return {
                    unreinforced: 'Unreinforced',
                    reinforced: 'Reinforced',
                    bcr: 'BCR',
                };
        }
    };

    // Helper function to get line colors
    const getLineColors = () => {
        return {
            unreinforced: '#8884d8',
            reinforced: '#82ca9d',
            bcr: '#ff7300',
        };
    };
    const renderLines = () => {
        const labels = getLegendLabels();
        const colors = getLineColors();

        // For unpaved/Giroud/Leng method, check if data has the special structure with series
        if ((method === 'unpaved' || method === 'giroud' || method === 'leng') && Array.isArray(chartData) && chartData.length > 0) {
            const firstItem = chartData[0];

            // Check if we have the special structure with individual series data
            if (firstItem && typeof firstItem === 'object' && 'name' in firstItem && 'data' in firstItem && 'color' in firstItem) {
                return chartData.map((item: any) => {
                    const lineData = item.data || [];

                    // Map the series name to our domain-specific labels
                    let seriesName = item.name;
                    if (item.name === 'Unreinforced') seriesName = labels.unreinforced;
                    else if (item.name === 'Reinforced') seriesName = labels.reinforced;
                    else if (item.name === 'BCR') seriesName = labels.bcr;

                    return (
                        <Line
                            key={item.name}
                            type="monotone"
                            data={lineData}
                            dataKey="y"
                            stroke={item.color}
                            name={seriesName}
                            dot={true}
                            strokeWidth={2}
                            activeDot={{ r: 6 }}
                            isAnimationActive={false}
                            connectNulls={true}
                        />
                    );
                });
            }

            // Check if we have the unpaved API structure with h, p_UNR, p_GTX, p_GG
            if (firstItem && typeof firstItem === 'object' && 'h' in firstItem && ('p_UNR' in firstItem || 'p_GTX' in firstItem || 'p_GG' in firstItem)) {
                return (
                    <>
                        <Line type="monotone" dataKey="p_UNR" stroke={colors.unreinforced} strokeWidth={2} activeDot={{ r: 6 }} name={labels.unreinforced} dot={false} />
                        <Line type="monotone" dataKey="p_GTX" stroke={colors.reinforced} strokeWidth={2} activeDot={{ r: 6 }} name={labels.reinforced} dot={false} />
                        <Line type="monotone" dataKey="p_GG" stroke={colors.bcr} strokeWidth={2} activeDot={{ r: 6 }} name={labels.bcr} dot={false} />
                    </>
                );
            }
        }

        // Default rendering for standard chart data structure
        return (
            <>
                <Line type="monotone" dataKey="unreinforced" stroke={colors.unreinforced} strokeWidth={2} activeDot={{ r: 6 }} name={labels.unreinforced} />
                {reinforcedData && <Line type="monotone" dataKey="reinforced" stroke={colors.reinforced} strokeWidth={2} activeDot={{ r: 6 }} name={labels.reinforced} />}
                {bcrData && <Line type="monotone" dataKey="bcr" stroke={colors.bcr} strokeWidth={2} activeDot={{ r: 6 }} name={labels.bcr} />}
            </>
        );
    };

    // Determine the correct dataKey for X-axis based on data structure
    const getXAxisDataKey = () => {
        if ((method === 'unpaved' || method === 'giroud' || method === 'leng') && Array.isArray(chartData) && chartData.length > 0) {
            const firstItem = chartData[0];
            if (firstItem && typeof firstItem === 'object' && 'h' in firstItem) {
                return 'h'; // Unpaved API uses 'h' for x-axis
            }
        }
        return 'x'; // Default for standard structure
    };

    return (
        <div style={{ width: '100%', height: 400 }} id="pavement-line-chart-results">
            <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 20, right: 120, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey={getXAxisDataKey()}
                        label={{ value: xAxisLabel, position: 'insideBottom', offset: -10, style: { fontSize: 18, fontFamily: 'Arial', fontWeight: 600 } }}
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tick={{ fontSize: 12, fontFamily: 'Arial' }}
                    />
                    <YAxis
                        label={{ value: yAxisLabel, angle: -90, position: 'left', dy: -35, style: { fontSize: 18, fontFamily: 'Arial', fontWeight: 600 } }}
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tick={{ fontSize: 12, fontFamily: 'Arial' }}
                    />
                    <Tooltip />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ paddingLeft: '10px', fontFamily: 'Arial' }} />
                    {renderLines()}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PavementLineChartComponent;
